import axios from 'axios';
import * as yup from 'yup';

import appSettings from '../../app-settings';
import { TAX_ID_REGEX } from '../../core/constants';

const ACCOUNTS_URL = `${appSettings.baseUrl}/accounts`;

const ARTIFICIAL_INTELLIGENCE_URL = `${appSettings.baseUrl}/artificial-intelligence`;

export const DOCUMENT_TYPES = {
  document: 'document',
  image: 'image',
};

export const DOCUMENT_CATEGORY_TYPES = {
  tax: 'tax',
  logo: 'logo',
  supporting: 'supporting',
};

export const createPresignedUploadUrl = async (
  filename,
  type = DOCUMENT_CATEGORY_TYPES.tax,
  documentType = DOCUMENT_TYPES.document,
) => {
  const response = await axios.post(`${ACCOUNTS_URL}/upload-url`, { type, filename, documentType });
  return response.data;
};

export const createPresignedDownloadUrl = async (documentKey) => {
  const response = await axios.post(`${ACCOUNTS_URL}/download-url`, { documentKey });
  return response.data;
};

export const updateAccount = async (account) => {
  const response = await axios.post(`${ACCOUNTS_URL}/current`, account);
  return response.data;
};

export const updateAccountDocument = async (taxDocumentKey) => {
  await axios.post(`${ACCOUNTS_URL}/current/document`, { taxDocumentKey });
};

export const getDocumentData = async (downloadUrl) => {
  const response = await axios.get(downloadUrl, { responseType: 'blob', headers: { Authorization: '' } });
  return response.data;
};

export const allowedFileTypes = { 'application/pdf': [] };

export const urlRegMatch = /^(https?:\/\/)?(?!www\.)[a-zA-Z0-9][a-zA-Z0-9.-]+[a-zA-Z0-9]$/;

export const formValidation = yup.object({
  logo: yup.mixed(),
  organizationName: yup.string().min(2, 'Invalid name.').required('Organization Name is required.'),
  legalName: yup.string().min(2, 'Invalid name.').required('Legal Name is required.'),
  customerSupportEmail: yup.string().email('Invalid email.').required('Customer Support Email is required.'),
  websiteUrl: yup.string().matches(urlRegMatch, 'Invalid URL.').required('Website URL is required.'),
  taxId: yup.string().matches(TAX_ID_REGEX, 'Invalid tax ID.').required('Tax ID is required.'),
  legalAddress: yup.string().min(2, 'Invalid address.').required('Legal Address is required.'),
  organizationDescription: yup.string().min(2, 'Invalid description.').required('Organization Mission is required.'),
  missionGeographicFocus: yup.string().required('Geographic Focus of Mission is required.'),
  contactsAddedLast12Months: yup.string().required('Field is required.'),
  amountRaisedLast12Months: yup.string().required('Field is required.'),
  taxDocumentKey: yup.string(),
});

export const getFormValues = (account) => ({
  logo: account?.logoUrl,
  organizationName: account?.organizationName ?? '',
  legalName: account?.legalName ?? '',
  customerSupportEmail: account?.customerSupportEmail ?? '',
  websiteUrl: account?.websiteUrl ?? '',
  taxId: account?.taxId ?? '',
  legalAddress: account?.legalAddress ?? '',
  organizationDescription: account?.organizationDescription ?? '',
  missionGeographicFocus: account?.missionGeographicFocus ?? '',
  contactsAddedLast12Months: account?.contactsAddedLast12Months ?? '',
  amountRaisedLast12Months: account?.amountRaisedLast12Months ?? '',
  taxDocumentKey: account?.taxDocumentKey ?? '',
});

export const EXECUTIVE_DIRECTOR_INPUT = {
  label: 'What is the name of your Executive Director?',
  excludeFromFeedback: true,
};

// Read by the generate-content agent to sign off emails. The agent looks the
// answer up by its profile key (label + helperText, see getProfileInputKey), so
// rewording either string orphans saved answers AND silently stops the agent
// using them: keep EMAIL_SIGNERS_PROFILE_KEY in
// backend/artificial-intelligence-api/generate-content-agent/src/sign-off.ts in
// step. No periods, since the key drops them.
export const EMAIL_SIGNERS_INPUT = {
  label: 'Who should sign off at the end of emails?',
  helperText:
    'Add one person per entry. We\'ll choose from these when writing emails. Example: "Sam Jones, Executive Director"',
  type: 'list',
  defaultItems: 1,
  excludeFromFeedback: true,
};

export const profileInputs = [
  {
    label: 'What are the URLs for your social media accounts?',
    type: 'list',
    defaultItems: 2,
    excludeFromFeedback: true,
  },
  {
    label: 'If donors could only know three things about your nonprofit, what would they be?',
    type: 'list',
    defaultItems: 3,
    minItems: 3,
  },
  {
    label: 'Why should a donor donate to your organization?',
    helperText: 'What is your pitch?',
  },
  {
    label: 'What date does your fiscal calendar end on?',
    type: 'date',
    excludeFromFeedback: true,
  },
  {
    label: 'On what date was your organization founded?',
    type: 'date',
    excludeFromFeedback: true,
  },
  {
    label: 'What are three annual dates that have a significance for your organization?',
    helperText:
      'Dates/events/holidays besides Giving Tuesday and the end of the calendar year. Please be as specific as possible.',
    type: 'list',
    defaultItems: 3,
    minItems: 3,
  },
  {
    label: 'What projects/programs is your organization currently working on?',
    type: 'list',
    defaultItems: 1,
  },
  {
    label: 'What goals are you fundraising towards?',
    type: 'list',
    defaultItems: 1,
  },
  {
    label: 'What would happen if people did not support your organization and you could not continue your work?',
  },
  {
    label: 'What are your key stats and impact numbers?',
    helperText: 'Focus on mission driven outcomes, lives served, programs delivered, policy wins, or measurable change',
    type: 'list',
    defaultItems: 1,
  },
  {
    label: 'Please list any specific use of funds:',
    helperText: 'For example, "$250 gets X" or "Last year we achieved Y after raising $___"',
    type: 'list',
    defaultItems: 1,
  },
  {
    label: 'What are three (or more) recent accomplishments or milestones?',
    type: 'list',
    defaultItems: 3,
    minItems: 3,
  },
  {
    label: 'Please share 3 to 4 specific personal stories or testimonials about your work',
    helperText: 'Include direct quotes, where possible',
    type: 'list',
    defaultItems: 3,
    minItems: 3,
  },
  {
    label:
      'Are there any key phrases or themes of your organization that should be regularly incorporated into donor communications?',
    type: 'list',
    defaultItems: 1,
  },
  {
    label: 'Are there any specific words or phrases we should exclude?',
    type: 'list',
    defaultItems: 1,
    excludeFromFeedback: true,
  },
  EXECUTIVE_DIRECTOR_INPUT,
  EMAIL_SIGNERS_INPUT,
  {
    label: 'Is there anything else we should know about your organization?',
    helperText: 'Anything that would be helpful for writing content for your organization',
    excludeFromFeedback: true,
  },
];

export const getProfileInputKey = (input) => {
  return (input.helperText ? `${input.label} ${input.helperText}` : input.label).replaceAll('.', '');
};

const hasListAnswer = (value) =>
  (Array.isArray(value) ? value : [value]).some((item) => typeof item === 'string' && item.trim());

// Profiles saved before the sign-off question existed already name their
// Executive Director, so start them with that signer instead of a blank
// required question. Only written to the profile when the user next saves (the
// agent applies the same fallback, so an unsaved prefill loses nothing).
export const getDefaultEmailSigners = (profile = {}) => {
  if (hasListAnswer(profile?.[getProfileInputKey(EMAIL_SIGNERS_INPUT)])) return undefined;

  const executiveDirector = profile?.[getProfileInputKey(EXECUTIVE_DIRECTOR_INPUT)];
  if (typeof executiveDirector !== 'string' || !executiveDirector.trim()) return undefined;

  return [`${executiveDirector.trim()}\nExecutive Director`];
};

export const profileFormValidation = yup.object(
  profileInputs.reduce((acc, input) => {
    if (input.type === 'list') {
      acc[getProfileInputKey(input)] = yup
        .array()
        .transform((value) => {
          if (Array.isArray(value)) return value.filter((item) => item?.trim());
          if (typeof value === 'string') return value?.trim() ? [value] : [];
          return [];
        })
        .min(input.minItems ?? 1, 'Field is required');
    } else {
      acc[getProfileInputKey(input)] = yup.string().required('Field is required');
    }
    return acc;
  }, {}),
);

const getAnswerForInput = (input, values) => {
  const key = getProfileInputKey(input);
  const value = values[key];

  if (input.type === 'list') {
    return Array.isArray(value) ? value.map((item) => item?.trim()).filter(Boolean) : [];
  }

  return typeof value === 'string' ? value.trim() : '';
};

const answerHasContent = (answer) => (Array.isArray(answer) ? answer.length > 0 : !!answer);

// Some answers are not worth grading for content quality:
// - date answers (a date is either set or not), and
// - inputs explicitly opted out via `excludeFromFeedback` (e.g. short factual
//   answers like the Executive Director's name).
export const isProfileFeedbackEligible = (input) => input.type !== 'date' && !input.excludeFromFeedback;

// Builds the request payload for the profile-feedback endpoint from the current
// form values. When `keys` is provided, only those inputs are included.
// Empty answers and date inputs are skipped.
export const buildProfileAnswers = (values, keys) => {
  return profileInputs.reduce((acc, input) => {
    if (!isProfileFeedbackEligible(input)) return acc;

    const key = getProfileInputKey(input);
    if (keys && !keys.includes(key)) return acc;

    const answer = getAnswerForInput(input, values);
    if (!answerHasContent(answer)) return acc;

    acc.push({
      key,
      question: input.label,
      guidance: input.helperText,
      type: input.type === 'list' ? 'list' : 'text',
      answer,
    });

    return acc;
  }, []);
};

export const getProfileFeedback = async (answers) => {
  const response = await axios.post(`${ARTIFICIAL_INTELLIGENCE_URL}/profile-feedback`, { answers });
  return response.data?.feedback ?? {};
};

// Merges freshly generated feedback on top of any existing (persisted) feedback.
// Feedback for questions not in the new batch is preserved, so editing one
// answer and saving does not wipe out feedback for the others.
export const mergeProfileFeedback = (existing = {}, incoming = {}) => ({
  ...existing,
  ...incoming,
});

// Average of all per-question scores, rounded. Returns null when there are no
// numeric scores yet. Used for the overall "AI Score" meter.
export const getOverallScore = (feedbackByKey = {}) => {
  const scores = Object.values(feedbackByKey)
    .map((item) => item?.score)
    .filter((score) => typeof score === 'number');

  if (scores.length === 0) return null;

  return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
};

// Quality-score bands mirror the grading rubric: 80-100 strong, 50-79 usable, 0-49 weak.
export const STRONG_SCORE = 80;

export const getScoreColorClassName = (score) => {
  if (score >= STRONG_SCORE) return '!bg-green-500';
  if (score >= 50) return '!bg-orange-500';
  return '!bg-red-500';
};

export const getScoreTextClassName = (score) => {
  if (score >= STRONG_SCORE) return 'text-green-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-500';
};

export const getScoreLabel = (score) => {
  if (score >= STRONG_SCORE) return 'Strong';
  if (score >= 50) return 'Could be stronger';
  return 'Inadequate';
};

// Two independent things can hold a question back: it may be unanswered (form
// validation) or answered but not yet graded "Strong" (the quality-score gate).
// These statuses fold both into one value so the form can mark every question
// without duplicating the rules.
export const PROFILE_QUESTION_STATUS = {
  missing: 'missing', // required and empty
  incomplete: 'incomplete', // list with fewer answers than minItems
  ungraded: 'ungraded', // answered, eligible for a score, none yet
  weak: 'weak', // answered and scored below STRONG_SCORE
  done: 'done', // blocks nothing
};

// Resolves one question's status from the current form values plus the feedback
// map. Derived from values rather than from `form.errors`, so an untouched empty
// question is marked immediately instead of only after it is blurred.
// `scoreRequired: false` (a customer whose minimum-score requirement has been
// waived) folds the score away entirely: an answered question reads `done` even
// when it scored below STRONG_SCORE, so the form never shows a blocking verdict
// on a question that no longer blocks anything.
export const getProfileQuestionStatus = (
  input,
  values = {},
  { feedbackByKey = {}, feedbackEnabled = true, scoreRequired = true } = {},
) => {
  const answer = getAnswerForInput(input, values);

  if (input.type === 'list') {
    const minItems = input.minItems ?? 1;

    if (answer.length < minItems) {
      return {
        status: answer.length === 0 ? PROFILE_QUESTION_STATUS.missing : PROFILE_QUESTION_STATUS.incomplete,
        answered: answer.length,
        minItems,
      };
    }
  } else if (!answerHasContent(answer)) {
    return { status: PROFILE_QUESTION_STATUS.missing };
  }

  if (scoreRequired && feedbackEnabled && isProfileFeedbackEligible(input)) {
    const score = feedbackByKey[getProfileInputKey(input)]?.score;

    if (typeof score !== 'number') return { status: PROFILE_QUESTION_STATUS.ungraded };
    if (score < STRONG_SCORE) return { status: PROFILE_QUESTION_STATUS.weak, score };
  }

  return { status: PROFILE_QUESTION_STATUS.done };
};

export const getProfileQuestionStatusLabel = ({ status, answered, minItems } = {}) => {
  switch (status) {
    case PROFILE_QUESTION_STATUS.missing:
      return 'Required';
    case PROFILE_QUESTION_STATUS.incomplete: {
      // Action, not a score: "Needs 2 more answers" beats "1 of 3 answers",
      // which reads like a status badge and leaves the user guessing.
      const remaining = minItems - answered;
      return `Needs ${remaining} more ${remaining === 1 ? 'answer' : 'answers'}`;
    }
    case PROFILE_QUESTION_STATUS.ungraded:
      return 'Not rated yet';
    case PROFILE_QUESTION_STATUS.weak:
      return 'Needs a stronger answer';
    default:
      return '';
  }
};

// Left-hand rail colour for a question block. Unanswered reads red (a hard
// validation failure), not-yet-strong reads orange (matching the score bands),
// finished reads green.
export const getProfileQuestionRailClassName = (status) => {
  switch (status) {
    case PROFILE_QUESTION_STATUS.missing:
    case PROFILE_QUESTION_STATUS.incomplete:
      return 'border-red-300';
    case PROFILE_QUESTION_STATUS.ungraded:
    case PROFILE_QUESTION_STATUS.weak:
      return 'border-orange-500';
    case PROFILE_QUESTION_STATUS.done:
      return 'border-green-300';
    default:
      return 'border-transparent';
  }
};

// DOM id for a question block, used to scroll the first unfinished question into
// view. Question keys are full sentences (spaces, quotes) so they cannot be used
// in a selector; the index can.
export const getProfileQuestionDomId = (index) => `profile-question-${index}`;

// Header roll-up: the completion percentage (answered questions / total, the
// same definition the meter always used) plus counts for the two gates.
export const getProfileCompletionSummary = (values = {}, options = {}) => {
  const statuses = profileInputs.map((input) => getProfileQuestionStatus(input, values, options));

  const unanswered = statuses.filter(
    ({ status }) => status === PROFILE_QUESTION_STATUS.missing || status === PROFILE_QUESTION_STATUS.incomplete,
  ).length;

  const notStrong = statuses.filter(
    ({ status }) => status === PROFILE_QUESTION_STATUS.ungraded || status === PROFILE_QUESTION_STATUS.weak,
  ).length;

  const total = statuses.length;
  const answered = total - unanswered;

  return { statuses, total, answered, unanswered, notStrong, percentage: total ? (answered / total) * 100 : 0 };
};

// Onboarding gate: returns true only when every feedback-eligible, answered
// question has generated feedback with a "Strong" (>= STRONG_SCORE) score.
// A question that is eligible and answered but has no feedback yet (still
// generating, or never graded) is treated as not-yet-strong so the user cannot
// proceed until all answers have been graded as strong.
export const areAllScoredQuestionsStrong = (values = {}, feedbackByKey = {}) => {
  return profileInputs.every((input) => {
    if (!isProfileFeedbackEligible(input)) return true;

    const answer = getAnswerForInput(input, values);
    if (!answerHasContent(answer)) return true; // unanswered: gated by form validation, not feedback

    const score = feedbackByKey[getProfileInputKey(input)]?.score;
    return typeof score === 'number' && score >= STRONG_SCORE;
  });
};

// The composed onboarding gate, so the in-form Next button and the onboarding
// step resolver cannot drift apart. Three things satisfy it:
//   - the score requirement was waived for this customer by DonorSpring staff
//     (tenant flag `bypassProfileScoreRequirement`, set in customer-management),
//   - feedback is disabled entirely (sandbox/demo, which has no backend), or
//   - every scored question is actually graded "Strong".
export const isProfileScoreGateSatisfied = (
  values = {},
  feedbackByKey = {},
  { scoreRequired = true, feedbackEnabled = true } = {},
) => {
  if (!scoreRequired || !feedbackEnabled) return true;
  return areAllScoredQuestionsStrong(values, feedbackByKey);
};
